import type * as THREE from 'three';
import type {
  MaterialWarmupOptions, PreloadPhaseId, PreloadRegistry, RenderSystem, SlicedJob, WarmupEntry,
} from '@/contracts';

/**
 * Preload orchestrator (docs/preload-design.md §2.2). Runs inside `Engine.boot`,
 * after every module's `init()`, before `loading.done()`. Modules register
 * warm-up work via `GameModule.preload(ctx, reg)`; the manager drains it in
 * named phases on a boot budget of ~12 ms per rAF so the loading bar and the
 * bobbing hard-hat never freeze.
 *
 * The `shaders` phase ends by awaiting the render module's warm-up rig
 * (`RenderSystem.warmup`) — that is what compiles every program permutation
 * before the first visible frame, so `loading.done()` cannot fire until the
 * warm-up completes.
 */

/** Fixed phase order. `shaders` is always last: it needs everything above. */
export const PRELOAD_PHASES: readonly PreloadPhaseId[] = [
  'textures', 'geometry', 'materials', 'physics', 'audio', 'shaders',
];

/** Per-rAF work budget behind the loading screen. */
const BOOT_BUDGET_MS = 12;

/** Watchdog: any single `step()` past this is logged in dev. */
const STEP_WARN_MS = 50;

interface PhaseJob { readonly label: string; readonly job: SlicedJob }

export class PreloadManager implements PreloadRegistry {
  private readonly phases = new Map<PreloadPhaseId, PhaseJob[]>();
  private readonly warmed = new Set<THREE.Material | THREE.Object3D>();
  private readonly entries: WarmupEntry[] = [];
  private stepSeq = 0;

  add(phase: PreloadPhaseId, label: string, job: SlicedJob): void {
    let list = this.phases.get(phase);
    if (!list) { list = []; this.phases.set(phase, list); }
    list.push({ label, job });
  }

  addStep(phase: PreloadPhaseId, label: string, run: () => void): void {
    this.add(phase, label, {
      id: `${phase}:${label}#${this.stepSeq++}`,
      step: () => { run(); return true; },
    });
  }

  warmMaterial(m: THREE.Material, opts?: MaterialWarmupOptions): void {
    if (this.warmed.has(m)) return;
    this.warmed.add(m);
    this.entries.push({ material: m, object: null, opts: opts ?? null });
  }

  warmObject(o: THREE.Object3D): void {
    if (this.warmed.has(o)) return;
    this.warmed.add(o);
    this.entries.push({ material: null, object: o, opts: null });
  }

  /** Everything registered for the shader warm-up rig, in registration order. */
  get warmupEntries(): readonly WarmupEntry[] { return this.entries; }

  /**
   * Drain every phase in order. `onProgress` receives overall 0..1 across all
   * phases plus the phase id — the caller maps that onto the loading bar.
   */
  async run(
    render: RenderSystem | undefined,
    onProgress: (frac: number, phase: PreloadPhaseId) => void,
  ): Promise<void> {
    const dev = import.meta.env.DEV;
    const n = PRELOAD_PHASES.length;
    let sliceStart = performance.now();
    const yieldFrame = async (): Promise<void> => {
      await new Promise((r) => requestAnimationFrame(r));
      sliceStart = performance.now();
    };

    for (let p = 0; p < n; p++) {
      const phase = PRELOAD_PHASES[p];
      onProgress(p / n, phase);
      const jobs = this.phases.get(phase);
      const total = jobs?.length ?? 0;
      if (jobs) {
        for (let j = 0; j < total; j++) {
          const { label, job } = jobs[j];
          for (;;) {
            const left = BOOT_BUDGET_MS - (performance.now() - sliceStart);
            if (left <= 0.5) {
              onProgress((p + j / total) / n, phase);
              await yieldFrame();
              continue;
            }
            const t0 = performance.now();
            let finished = true;
            try {
              finished = job.step(left);
            } catch (err) {
              console.error(`[preload] ${phase}/${label} (${job.id}) threw — skipped`, err);
            }
            if (dev) {
              const took = performance.now() - t0;
              if (took > STEP_WARN_MS) {
                console.warn(`[preload] ${phase}/${label} step took ${took.toFixed(0)} ms (budget ${BOOT_BUDGET_MS} ms) — slice it finer`);
              }
            }
            if (finished) break;
          }
          onProgress((p + (j + 1) / total) / n, phase);
        }
      }

      if (phase === 'shaders' && render && typeof render.warmup === 'function') {
        try {
          await render.warmup(this.entries);
        } catch (err) {
          // No fail states: a broken warm-up means first-use compile stalls,
          // not a broken game.
          console.error('[preload] shader warm-up failed — continuing without it', err);
        }
      }
      await yieldFrame();
    }
    onProgress(1, 'shaders');
  }
}
